import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Sparkles, Zap, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface UpgradeBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  onUpgradeClick: () => void;
}
export function UpgradeBottomSheet({
  isOpen,
  onClose,
  message = "You've used your 3 free daily rewrites. Go Pro for unlimited communication coaching.",
  onUpgradeClick
}: UpgradeBottomSheetProps) {
  // Prevent scrolling when bottom sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-2148">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]" data-id="element-2149" />

          {/* Bottom Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-0 left-0 right-0 z-[101] flex justify-center pointer-events-none" data-id="element-2150">
            <div className="w-full max-w-md bg-[#0a0a0a] border-t border-x border-[#FF0000]/30 rounded-t-3xl overflow-hidden pointer-events-auto shadow-[0_-10px_40px_rgba(255,0,0,0.15)] pb-safe" data-id="element-2151">
              {/* Drag Handle (Visual only) */}
              <div className="w-full flex justify-center pt-4 pb-2" data-id="element-2152">
                <div className="w-12 h-1.5 bg-gray-800 rounded-full" data-id="element-2153" />
              </div>

              <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/5" data-id="element-2154">
                <X size={20} data-id="element-2155" />
              </button>

              <div className="px-6 pt-2 pb-8" data-id="element-2156">
                <div className="flex justify-center mb-6" data-id="element-2157">
                  <div className="relative" data-id="element-2158">
                    <div className="absolute inset-0 bg-[#FF0000] blur-xl opacity-20 rounded-full" data-id="element-2159" />
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FF0000]/20 to-[#FF0000]/5 border border-[#FF0000]/30 rounded-2xl flex items-center justify-center relative z-10 shadow-[0_0_15px_rgba(255,0,0,0.2)]" data-id="element-2160">
                      <Crown className="w-8 h-8 text-[#FF0000]" data-id="element-2161" />
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-black text-white text-center mb-3 tracking-tight uppercase" data-id="element-2162">
                  Daily Limit <span className="text-[#FF0000]" data-id="element-2163">Reached</span>
                </h2>

                <p className="text-gray-300 text-center mb-8 font-mono text-sm leading-relaxed" data-id="element-2164">
                  {message}
                </p>

                <div className="space-y-3 mb-8" data-id="element-2165">
                  {['Unlimited message rewrites', 'Access to all emotional tones', 'Advanced toxicity analysis', 'Priority AI processing'].map((benefit, i) => <div key={i} className="flex items-center text-sm text-gray-300" data-id="element-2166">
                      <CheckCircle2 className="w-4 h-4 text-[#FF0000] mr-3 flex-shrink-0" data-id="element-2167" />
                      <span className="font-mono" data-id="element-2168">{benefit}</span>
                    </div>)}
                </div>

                <button onClick={() => {
              onClose();
              onUpgradeClick();
            }} className="w-full bg-[#FF0000] hover:bg-red-600 text-white font-bold py-4 px-6 rounded-xl shadow-[0_0_20px_rgba(255,0,0,0.4)] transition-all flex items-center justify-center group uppercase tracking-wider" data-id="element-2169">
                  <Zap className="w-5 h-5 mr-2 fill-white group-hover:scale-110 transition-transform" data-id="element-2170" />
                  Upgrade to Pro
                </button>

                <button onClick={onClose} className="w-full mt-4 py-3 text-gray-500 hover:text-white font-mono text-sm uppercase tracking-wider transition-colors" data-id="element-2171">
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}